import { SearchFilterSchema } from "../common/CustomFilter";
import _, { parseInt } from "lodash";

export const modifySearch = (
	searchObj: SearchFilterSchema,
	searchCriteria: SearchFilterSchema[],
) => {
	// modify the searchCriteria if key is same updating the value
	const modifySearchCriteria = searchCriteria?.map((item: SearchFilterSchema) =>
		item?.key === searchObj?.key ?
			{ ...item, value: searchObj?.value }
			: item);
	// checking key is present in the array
	const isKey = searchCriteria?.some((item: SearchFilterSchema) => item?.key === searchObj?.key);
	// if isKey present in array updating modifySearchCriteria else with modifySearchCriteria another object pushed
	return isKey ? [...modifySearchCriteria] : [...modifySearchCriteria, searchObj];
};

export const customFilter = (arr: any[], searchCriteria: SearchFilterSchema[], logicalSearchKeys: string[] = []) => {
	// Filter out search criteria that have both key and non-empty value
	const searchFilter = searchCriteria.filter((item) => item.key && item.value !== "");

	// Filter the array based on the search criteria
	const filterArr = arr?.filter((ele) => searchFilter.every((item) => {
		const eleValue = ele?.[item.key];
		const itemValue = item?.value;

		if (
			logicalSearchKeys.includes(item.key)
			&& eleValue !== undefined
			&& typeof eleValue === "number"
			&& [">", "<", "="].some((operator) => itemValue.startsWith(operator))
		) {
			if (itemValue.startsWith(">")) {
				return eleValue > parseInt(itemValue.slice(1));
			} else if (itemValue.startsWith("<")) {
				return eleValue < parseInt(itemValue.slice(1));
			} else if (itemValue.startsWith("=")) {
				return eleValue === parseInt(itemValue.slice(1));
			}
		} else {
			return eleValue?.toString().toLowerCase().includes(itemValue.toLowerCase().toLowerCase());
		}

		return false;
	}));

	return searchFilter.length ? filterArr : arr;
};

export const customUniqBy = (arr: any[], key: string) => {
	return _.uniqBy(arr, (item: any) => item?.[key]);
};

export const getGroupedOptions = (data: string[], label: string, isFormate = true) => {
	return {
		label,
		options: isFormate ? data.map((item: any) => ({
			label: item,
			value: item,
		})) : data,
	};
};

export function formatIndianStyle(value: any) {
	// Check if the value contains any alphabetic characters or is a date-like string
	if (/[a-zA-Z]/.test(value)) {
		return value;
	}

	// Check if it's a date-like format, e.g., 25-12-2024
	const datePattern = /^\d{1,2}-\d{1,2}-\d{4}$/;
	if (datePattern.test(value)) {
		return value;
	}

	// First, remove any commas from the input if it's in string form
	let numStr = value.toString().replace(/,/g, '');

	// Ensure that the value is a valid number (it might be a float)
	if (isNaN(numStr)) {
		return value;  // If it's not a valid number, return the input as is
	}

	// Handle negative numbers by storing the negative sign separately
	let isNegative = false;
	if (numStr[0] === '-') {
		isNegative = true;
		numStr = numStr.slice(1);  // Remove the negative sign temporarily for formatting
	}

	// Split the number into integer and decimal parts
	let [integerPart, decimalPart] = numStr.split('.');

	// Declare integerFormatted
	let integerFormatted: string;

	// Handle the integer part for Indian style formatting
	if (integerPart.length > 3) {
		let lastThree = integerPart.slice(-3);  // Last 3 digits (thousands part)
		let rest = integerPart.slice(0, integerPart.length - 3);  // All digits except the last 3
		rest = rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',');  // Add commas every 2 digits to the remaining part
		integerFormatted = rest + ',' + lastThree;  // Combine the two parts
	} else {
		integerFormatted = integerPart;  // No comma needed for numbers with 3 or fewer digits
	}

	// If the number was negative, add the negative sign back
	if (isNegative) {
		integerFormatted = '-' + integerFormatted;
	}

	// If there's a decimal part, add it back
	if (decimalPart) {
		return integerFormatted + '.' + decimalPart;
	}

	return integerFormatted;
}



export const parseValue = (value: string) => {
	// Remove commas and convert to number
	try {
		return parseFloat(value.replace(/,/g, ""));
	} catch (error) {
		return 0
	}
};


export function formatDisplayDate(value: any, separator: string = "/") {
	if (!value) {
		return "";
	}

	const rawValue = String(value).trim();
	let day = "";
	let month = "";
	let year = "";

	if (/^\d{4}-\d{2}-\d{2}/.test(rawValue)) {
		const parts = rawValue.slice(0, 10).split("-");
		[year, month, day] = parts;
	}
	else if (/^\d{2}[-/]\d{2}[-/]\d{4}$/.test(rawValue)) {
		const parts = rawValue.split(/[-/]/);
		const first = Number(parts[0]);
		const second = Number(parts[1]);
		year = parts[2];

		if (first > 12) {
			[day, month] = parts;
		}
		else if (second > 12) {
			[month, day] = parts;
		}
		else {
			[day, month] = parts;
		}
	}
	else {
		const parsedDate = new Date(rawValue);
		if (!Number.isNaN(parsedDate.getTime())) {
			day = String(parsedDate.getDate()).padStart(2, "0");
			month = String(parsedDate.getMonth() + 1).padStart(2, "0");
			year = String(parsedDate.getFullYear());
		}
	}

	if (!(day && month && year)) {
		return rawValue;
	}

	return `${String(day).padStart(2, "0")}${separator}${String(month).padStart(2, "0")}${separator}${year}`;
}


export const orderedData = (data: any, preferredOrder: any) => {
	return data?.map((item: any) => {
		const orderedItem: any = {};

		preferredOrder?.forEach((key: any) => {
			if (item.hasOwnProperty(key)) {
				// Apply the formatting logic here for each key's value
				orderedItem[key] = formatIndianStyle(item[key]) + (key?.endsWith("perc") ? "%" : "");
			}
		});

		// Optionally, you can add any keys from the original item that are not in the preferredOrder to the end
		Object?.keys(item)?.forEach(key => {
			if (!preferredOrder.includes(key)) {
				// Apply formatting to any remaining keys
				orderedItem[key] = formatIndianStyle(item[key]) + (key?.endsWith("perc") ? "%" : "");
			}
		});

		return orderedItem;
	});
};

export const getTimestamp = () => {
	const now = new Date();
	const day = String(now.getDate()).padStart(2, '0'); // Add leading zero if needed
	const month = String(now.getMonth() + 1).padStart(2, '0'); // Months are zero-based
	const year = now.getFullYear();
	const hours = String(now.getHours()).padStart(2, '0');
	const minutes = String(now.getMinutes()).padStart(2, '0');
	const seconds = String(now.getSeconds()).padStart(2, '0');

	return `${day}${month}${year}${hours}${minutes}${seconds}`;
};












// Diff Helpers

export const AllQuarters = ["Q1", "Q2", "Q3", "Q4"]
export const AllMonths = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]


export function getMonthsFromQuarters(quartersAndMonths: any) {
	const quartersMap: any = {
		Q1: AllMonths.slice(0, 3),  // Jan, Feb, Mar
		Q2: AllMonths.slice(3, 6),  // Apr, May, Jun
		Q3: AllMonths.slice(6, 9),  // Jul, Aug, Sep
		Q4: AllMonths.slice(9, 12)  // Oct, Nov, Dec
	};

	// Use a Set to store months without duplicates
	let resultSet = new Set();

	quartersAndMonths.forEach((item: any) => {
		if (quartersMap[item]) {
			// If item is a quarter, add its months to the Set
			quartersMap[item].forEach((month: any) => resultSet.add(month));
		} else if (AllMonths.includes(item)) {
			// If item is a month, add it directly to the Set
			resultSet.add(item);
		}
	});

	// Convert the Set back to an array for the final result
	return Array.from(resultSet);
}

export function getFilteredValues(inputValues: any, filterOutValues: any, allValues: any, isMonth = false) {
	if (isMonth && filterOutValues?.length > 0) {
		if (inputValues?.length === 0 && filterOutValues?.every((item: any) => item?.startsWith("Q"))) {
			inputValues = AllQuarters;
		}

		else if ((inputValues?.length === 0 || !(inputValues?.every((item: any) => item?.startsWith("Q")))) && filterOutValues?.some((item: any) => item?.startsWith("Q"))) {
			filterOutValues = getMonthsFromQuarters(filterOutValues)
		}
	}

	if (filterOutValues?.length > 0) {
		if (inputValues?.length > 0) {
			return inputValues.filter((value: any) => !filterOutValues.includes(value));
		}
		return allValues.filter((value: any) => !filterOutValues.includes(value));
	}

	return inputValues; // If no filterOut values, return the full list
}

export function getData(item: any) {
	return localStorage.getItem(item);
}

export function storeData(item: any, val: any) {
	return localStorage.setItem(item, val);
}

export function removeData(item: any) {
	return localStorage.removeItem(item);
}
